import map from 'lodash/map.js';

import Model from '@/models/Model.js';
import ObjectiveCriteria from '@/models/ObjectiveCriteria.js';

// Tail of the in-flight save chain for each objective, keyed by its subject
// (week/section) identity. Debounced edits to the title, description, heading
// and criteria each fire their own save; on large templates those requests are
// slow enough to overlap, and @vuex-orm/plugin-axios auto-persists every
// response into the store. When a slower earlier request finished last it would
// overwrite the store — and be committed by the server — *after* a newer edit,
// reverting the objective to stale text. Chaining the requests guarantees they
// are sent (and their responses applied) strictly in the order the user typed.
//
// Chaining alone is not enough: the criteria editor renders straight from the
// store, so a superseded request auto-persisting its (now stale) echo while the
// user keeps typing would still revert the field mid-edit and drop words. So
// only the *tail* save — the most recent one queued — applies its response;
// earlier saves still reach the server in order but skip the store write.
const saveChains = new Map();

export default class Objective extends Model {
    static entity = 'objectives';

    static modelName = 'Objective';

    static fields() {
        return {
            id: this.attr(null),
            courseable_id: this.attr(null),
            courseable_type: this.attr(null),
            subject_id: this.attr(null),
            subject_type: this.attr(null),
            title: this.attr(null),
            description: this.attr(null),
            heading: this.attr(null),
            allow_objective_submissions: this.attr(null),

            ...super.fields(),

            courseable: this.morphTo('courseable_id', 'courseable_type_entity'),
            subject: this.morphTo('subject_id', 'subject_type_entity'),
            criterias: this.hasMany('objective_criterias', 'objective_id'),
        };
    }

    async remove() {
        const payload = this.$toJson();

        payload.criterias = ObjectiveCriteria.query()
            .where('objective_id', this.id)
            .where(criteria => !!criteria.description)
            .orderBy('order')
            .get()
            .map((criteria, index) => {
                criteria.order = index + 1;

                if (typeof criteria.objective_id === 'string') {
                    delete criteria.objective_id;
                }
                return criteria;
            });

        if (payload.criterias.length !== 0) {
            const response = await Objective.api().put(`/api/objectives/${this.id}`, payload);

            const ids = map(response.entities.objective_criterias, 'id');

            await ObjectiveCriteria.delete(criteria => !ids.includes(criteria.id) && criteria.objective_id === this.id);
        } else {
            await Objective.delete(this.id);
            await Objective.api().delete(`/api/objectives/${this.id}`);
        }
    }

    async save() {
        // Snapshot the payload synchronously, from the current store state, so a
        // queued save sends exactly what the user had typed when it was triggered —
        // never a value an out-of-order response may have since written back.
        const payload = this.$toJson();

        payload.criterias = ObjectiveCriteria.query()
            .where('objective_id', this.id)
            .where(criteria => !!criteria.description)
            .orderBy('order')
            .get()
            .map((criteria, index) => {
                criteria.order = index + 1;

                if (typeof criteria.objective_id === 'string') {
                    delete criteria.objective_id;
                }
                return criteria;
            });

        if (!payload.criterias.length) {
            return;
        }

        const objectiveId = this.id;
        const key = `${this.subject_type}:${this.subject_id}`;

        // Send this request only after any earlier save for the same objective has
        // settled, so requests reach the server in edit order. A failed earlier save
        // must not block later edits, so swallow its rejection before chaining.
        const previous = saveChains.get(key) ?? Promise.resolve();
        const chained = previous
            .catch(() => {})
            .then(() => {
                // If a newer save for this objective was queued while this one waited,
                // this request is no longer the tail: still send it to preserve edit
                // order on the server, but don't let its stale response overwrite the
                // latest edit in the store — the tail save applies the final state.
                const isTail = saveChains.get(key) === chained;

                return this.persist(payload, objectiveId, isTail);
            });

        saveChains.set(key, chained);

        try {
            await chained;
        } finally {
            // Drop the chain once this was the last queued save so it can be
            // garbage collected and a fresh edit starts a clean chain.
            if (saveChains.get(key) === chained) {
                saveChains.delete(key);
            }
        }
    }

    async persist(payload, objectiveId, applyResponse = true) {
        const newObjective = typeof objectiveId !== 'number';

        // A superseded (non-tail) save must not auto-persist its now-stale response,
        // or it would revert the store-bound editor to older text. A brand-new
        // objective is the exception: its POST response carries the server-assigned
        // id that later saves rely on, so it must always be applied.
        const save = applyResponse || newObjective;

        const response = newObjective
            ? await Objective.api().post('/api/objectives', payload, { save })
            : await Objective.api().put(`/api/objectives/${objectiveId}`, payload, { save });

        // Only reconcile criteria when the response was applied; the tail save
        // performs the authoritative cleanup against the latest server state.
        if (save) {
            const ids = map(response.entities.objective_criterias, 'id');

            await ObjectiveCriteria.delete(
                criteria => !ids.includes(criteria.id) && criteria.objective_id === objectiveId
            );
        }
    }
}
